import React, { useState, useMemo, useCallback } from 'react';
import { GitBranch } from '../../hooks/useGit';

type BranchCompareModalProps = {
  isOpen: boolean;
  onClose: () => void;
  branches: GitBranch[];
  currentBranch: string;
  workingFiles: Record<string, string>;
  persistedFiles: Record<string, string>;
  onSelectFile: (filePath: string) => void;
};

type FileDiff = {
  path: string;
  status: 'added' | 'deleted' | 'modified';
  additions: number;
  deletions: number;
};

function computeDiff(
  baseFiles: Record<string, string>,
  compareFiles: Record<string, string>
): FileDiff[] {
  const diffs: FileDiff[] = [];
  const allPaths = Array.from(new Set([...Object.keys(baseFiles), ...Object.keys(compareFiles)]));

  for (const path of allPaths) {
    const baseContent = baseFiles[path];
    const compareContent = compareFiles[path];

    if (baseContent === undefined && compareContent !== undefined) {
      // Added in compare
      const lines = compareContent.split('\n').length;
      diffs.push({ path, status: 'added', additions: lines, deletions: 0 });
    } else if (baseContent !== undefined && compareContent === undefined) {
      // Deleted in compare
      const lines = baseContent.split('\n').length;
      diffs.push({ path, status: 'deleted', additions: 0, deletions: lines });
    } else if (baseContent !== compareContent) {
      // Modified
      const baseLines = baseContent?.split('\n') || [];
      const compareLines = compareContent?.split('\n') || [];

      // Simple line diff counting
      let additions = 0;
      let deletions = 0;

      const maxLines = Math.max(baseLines.length, compareLines.length);
      for (let i = 0; i < maxLines; i++) {
        if (baseLines[i] !== compareLines[i]) {
          if (baseLines[i] !== undefined) deletions++;
          if (compareLines[i] !== undefined) additions++;
        }
      }

      diffs.push({ path, status: 'modified', additions, deletions });
    }
  }

  return diffs.sort((a, b) => a.path.localeCompare(b.path));
}

export const BranchCompareModal = React.memo(function BranchCompareModal({
  isOpen,
  onClose,
  branches,
  currentBranch,
  workingFiles,
  persistedFiles,
  onSelectFile,
}: BranchCompareModalProps) {
  const [baseBranch, setBaseBranch] = useState(currentBranch);
  const [compareBranch, setCompareBranch] = useState('');
  const [selectedFile, setSelectedFile] = useState<string | null>(null);

  // For demo purposes, we simulate branches with different file states
  // In a real implementation, this would fetch files from each branch
  const branchFiles = useMemo(() => {
    const files: Record<string, Record<string, string>> = {};

    // Current branch has working files
    files[currentBranch] = { ...persistedFiles, ...workingFiles };

    // Simulate other branches with persisted files (baseline)
    for (const branch of branches) {
      if (branch.name !== currentBranch) {
        // Other branches show persisted state (simulated as baseline)
        files[branch.name] = { ...persistedFiles };
      }
    }

    return files;
  }, [branches, currentBranch, workingFiles, persistedFiles]);

  const diff = useMemo(() => {
    if (!baseBranch || !compareBranch || baseBranch === compareBranch) {
      return [];
    }

    const baseFiles = branchFiles[baseBranch] || {};
    const compareFiles = branchFiles[compareBranch] || {};

    return computeDiff(baseFiles, compareFiles);
  }, [baseBranch, compareBranch, branchFiles]);

  const stats = useMemo(() => {
    const totalAdditions = diff.reduce((sum, d) => sum + d.additions, 0);
    const totalDeletions = diff.reduce((sum, d) => sum + d.deletions, 0);
    const added = diff.filter(d => d.status === 'added').length;
    const deleted = diff.filter(d => d.status === 'deleted').length;
    const modified = diff.filter(d => d.status === 'modified').length;

    return { totalAdditions, totalDeletions, added, deleted, modified };
  }, [diff]);

  const handleFileClick = useCallback((filePath: string) => {
    setSelectedFile(filePath);
    onSelectFile(filePath);
  }, [onSelectFile]);

  const swapBranches = useCallback(() => {
    const temp = baseBranch;
    setBaseBranch(compareBranch);
    setCompareBranch(temp);
  }, [baseBranch, compareBranch]);

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal branch-compare-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '700px', maxHeight: '80vh' }}
      >
        <div className="modal-header">
          <div className="modal-title">Compare Branches</div>
          <button className="modal-close" onClick={onClose}>
            x
          </button>
        </div>

        <div className="modal-body" style={{ padding: '16px' }}>
          {/* Branch selectors */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
                Base branch
              </label>
              <select
                value={baseBranch}
                onChange={(e) => setBaseBranch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  color: 'var(--text)',
                  fontSize: '13px',
                }}
              >
                {branches.map((branch) => (
                  <option key={branch.name} value={branch.name}>
                    {branch.name} {branch.current ? '(current)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={swapBranches}
              style={{
                padding: '8px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                color: 'var(--text)',
                cursor: 'pointer',
                marginTop: '16px',
              }}
              title="Swap branches"
            >
              ⇄
            </button>

            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
                Compare branch
              </label>
              <select
                value={compareBranch}
                onChange={(e) => setCompareBranch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  color: 'var(--text)',
                  fontSize: '13px',
                }}
              >
                <option value="">Select branch...</option>
                {branches.filter(b => b.name !== baseBranch).map((branch) => (
                  <option key={branch.name} value={branch.name}>
                    {branch.name} {branch.current ? '(current)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Stats */}
          {diff.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '16px',
              padding: '12px',
              background: 'var(--bg-secondary)',
              borderRadius: '4px',
              marginBottom: '16px',
              fontSize: '12px',
            }}>
              <div>
                <span style={{ color: 'var(--muted)' }}>Files changed: </span>
                <span style={{ fontWeight: 600 }}>{diff.length}</span>
              </div>
              <div>
                <span style={{ color: 'var(--success)' }}>+{stats.totalAdditions}</span>
                {' / '}
                <span style={{ color: 'var(--error)' }}>-{stats.totalDeletions}</span>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
                {stats.added > 0 && (
                  <span style={{ color: 'var(--success)' }}>
                    {stats.added} added
                  </span>
                )}
                {stats.modified > 0 && (
                  <span style={{ color: 'var(--warning)' }}>
                    {stats.modified} modified
                  </span>
                )}
                {stats.deleted > 0 && (
                  <span style={{ color: 'var(--error)' }}>
                    {stats.deleted} deleted
                  </span>
                )}
              </div>
            </div>
          )}

          {/* File list */}
          <div style={{
            maxHeight: '350px',
            overflow: 'auto',
            border: '1px solid var(--border)',
            borderRadius: '4px',
          }}>
            {!compareBranch ? (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: 'var(--muted)',
                fontSize: '13px',
              }}>
                Select a branch to compare
              </div>
            ) : diff.length === 0 ? (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: 'var(--muted)',
                fontSize: '13px',
              }}>
                No differences between branches
              </div>
            ) : (
              diff.map((file) => (
                <div
                  key={file.path}
                  onClick={() => handleFileClick(file.path)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                    background: selectedFile === file.path ? 'var(--panel-subtle)' : 'transparent',
                  }}
                >
                  <span style={{
                    width: '16px',
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: '12px',
                    color: file.status === 'added' ? 'var(--success)' :
                           file.status === 'deleted' ? 'var(--error)' :
                           'var(--warning)',
                  }}>
                    {file.status === 'added' ? 'A' :
                     file.status === 'deleted' ? 'D' : 'M'}
                  </span>
                  <span style={{ flex: 1, fontSize: '13px', fontFamily: 'monospace' }}>
                    {file.path}
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--success)' }}>
                    +{file.additions}
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--error)' }}>
                    -{file.deletions}
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Info note */}
          <div style={{
            marginTop: '12px',
            fontSize: '11px',
            color: 'var(--muted)',
            fontStyle: 'italic',
          }}>
            Note: Click on a file to view it. In a connected environment, this compares actual branch contents.
          </div>
        </div>

        <div className="modal-footer">
          <button className="pill-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
});

export default BranchCompareModal;
